import { Matrix4 } from "../../../imports/avm";
import { VisibilityState } from "../InstanceCollectionBase";
import { Node3D } from "./Node3D";
import { Model } from "../../../application/Model";

/** @import { BufferGeometry, Material } from "../../../imports/avs" */

// Module-level temps for InstanceNode3D.
// _tempLocalMatrix: compose pos/quat/scale into local matrix (consumed before child recursion).
// _tempWorldMatrix: compute world matrix to write to instance collection.
// _sharedReadMatrix: used internally to read parent world matrices without allocation.
const _tempLocalMatrix = new Matrix4();
const _tempWorldMatrix = new Matrix4();
const _sharedReadMatrix = new Matrix4();

/**
 * Scene graph node that represents a single geometry instance.
 * @extends {Node3D}
 */
export class InstanceNode3D extends Node3D {

  /** @type {number | null} */
  #instanceId = null;

  /**
   * Creates a new detached instance node based on the given geometry and material.
   * @param {BufferGeometry} [geometry]
   * @param {Material} [material]
   */
  constructor(geometry, material) {
    super(true); // skip _matrixWorld own property — world transform lives in the instance collection

    this._detachedState.geometry = geometry;
    this._detachedState.material = material;
  }

  /** @returns {number | null} The instance ID in the instance collection, or null if detached. */
  get instanceId() {
    return this.#instanceId;
  }

  /**
   * Reads the world transform matrix into {@link target}.
   * When attached, reads from the instance collection (source of truth).
   * When detached, copies from detachedState.
   *
   * @param {Matrix4} [target] Matrix to write into. Recommended for safe, allocation-free reads.
   * @returns {Matrix4} The target filled with the world matrix, or a new matrix if no target is provided.
   */
  getTransformWorld(target) {
    if (this.#instanceId !== null && this._instanceCollection) {
      const out = target || new Matrix4();
      this._instanceCollection.getTransform(this.#instanceId, out);
      return out;
    }
    const mw = this._detachedState.matrixWorld ??= new Matrix4();
    return target ? target.copy(mw) : mw.clone();
  }

  /**
   * @returns {VisibilityState} The visibility state.
   */
  getVisibilityState() {
    return this._detachedState ? this._detachedState.visibilityState : this._instanceCollection?.getVisibilityState(this.#instanceId);
  }

  /**
   * Sets the visibility state for this node and its children.
   * @param {VisibilityState} value
   * @returns {void}
   */
  setVisibilityState(value) {
    if (!this._detachedState) {
      this._instanceCollection.setVisibilityState(this.#instanceId, value);
    }
    super.setVisibilityState(value);
  }

  /**
   * Initializes this node with the given instance collection.
   * @param {InstanceCollectionBase} instanceCollection
   * @param {boolean} [updateMatrices=true] If true, updateTransformWorld is called after initialization.
   * @returns {this}
   */
  _initialize(instanceCollection, updateMatrices = true) {
    // If we're initializing from an existing instance (fromInstance case), don't update matrices
    // in super._initialize - we need to read the instance's transform first before any updates
    const initFromExistingInstance = this.#instanceId !== null;

    super._initialize(instanceCollection, initFromExistingInstance ? false : updateMatrices);

    const tmpState = this._detachedState;
    this._detachedState = undefined;

    // Create fragment on first model assignment if we have enough info
    if (tmpState) {
      if (this.#instanceId === null && tmpState.geometry && tmpState.material) {
        // initialize node based on geometry and material
        // Note: matrixWorld is in tmpState.matrixWorld (instanceId still null), updated by super._initialize

        const createdId = this._instanceCollection.add(tmpState.geometry, tmpState.material, tmpState.matrixWorld);
        if (createdId !== undefined) {
          this.#instanceId = createdId;

          // Apply visibility state that was set before initialization
          this._instanceCollection.setVisibilityState(this.#instanceId, tmpState.visibilityState);
        }
      } else if (initFromExistingInstance) {
        // initialize node based on existing instance - read transform from instance collection
        this._instanceCollection.getTransform(this.#instanceId, _tempWorldMatrix);
        const parentWorld = this.parent?.getTransformWorld(_sharedReadMatrix);
        if (parentWorld) {
          _tempLocalMatrix.copy(parentWorld).invert().multiply(_tempWorldMatrix);
        } else {
          _tempLocalMatrix.copy(_tempWorldMatrix);
        }
        _tempLocalMatrix.decompose(this.position, this.quaternion, this.scale);

        // Don't call updateTransformWorld - the instance already has the correct transform
      }
    }

    return this;
  }

  /**
   * @returns {Material} The material of the instance represented by this node.
   */
  getMaterial() {
    return this._detachedState ? this._detachedState.material : this._instanceCollection?.getMaterial(this.#instanceId);
  }

  /**
   * Sets material on the instance represented by this node.
   * @param {Material} material
   * @returns {void}
   */
  setMaterial(material) {
    if (this._detachedState) {
      this._detachedState.material = material;
      return;
    }

    this._instanceCollection?.setMaterial(this.#instanceId, material);
  }

  /**
   * @returns {BufferGeometry} The geometry of the instance represented by this node.
   */
  getGeometry() {
    return this._detachedState ? this._detachedState.geometry : this._instanceCollection?.getGeometry(this.#instanceId);
  }

  /**
   * Sets the geometry of the instance represented by this node.
   * @param {BufferGeometry} geometry
   * @returns {void}
   */
  setGeometry(geometry) {
    if (this._detachedState) {
      this._detachedState.geometry = geometry;
      return;
    }
    this._instanceCollection?.setGeometry(this.#instanceId, geometry);
  }

  /**
   * Detaches this node from the instance collection.
   * @param {number[]} instanceIdsToRemove - Array to gather instance IDs that should be removed from the collection.
   * @internal
   */
  _detachFromInstanceCollection(instanceIdsToRemove) {
    const instanceCollection = this._instanceCollection;

    super._detachFromInstanceCollection(instanceIdsToRemove);

    if (instanceCollection && this.#instanceId !== null) {
      // Save world matrix and instance data into _detachedState (re-created by super call)
      this._detachedState.matrixWorld = new Matrix4();
      instanceCollection.getTransform(this.#instanceId, this._detachedState.matrixWorld);
      this._detachedState.geometry = instanceCollection.getGeometry(this.#instanceId);
      this._detachedState.material = instanceCollection.getMaterial(this.#instanceId);
      instanceIdsToRemove.push(this.#instanceId);
    }

    this.#instanceId = null;
  }

  /**
   * Updates world matrix and applies it to the represented instance.
   * Computations are performed immediately and propagated to all affected descendants.
   *
   * @param {boolean} [force=false] If true, forces update regardless of dirty flag.
   * @param {Matrix4} [_parentWorldMatrix=null] Internal: parent's world matrix passed during recursion.
   * @returns {void}
   */
  updateTransformWorld(force = false, _parentWorldMatrix = null) {
    const needsUpdate = this._dirty || force;

    if (needsUpdate) {
      this.getTransformLocal(_tempLocalMatrix);

      const parentWorld = _parentWorldMatrix || this.parent?.getTransformWorld(_sharedReadMatrix);

      if (this.#instanceId !== null && this._instanceCollection) {
        // Attached: compute world matrix into temp, write directly to instance collection
        if (parentWorld) {
          _tempWorldMatrix.multiplyMatrices(parentWorld, _tempLocalMatrix);
        } else {
          _tempWorldMatrix.copy(_tempLocalMatrix);
        }
        this._instanceCollection.setTransform(this.#instanceId, _tempWorldMatrix);
      } else {
        // Detached (e.g. during initialization before instanceId is assigned):
        // store in _detachedState.matrixWorld (lazily allocated)
        const mw = this._detachedState.matrixWorld ??= new Matrix4();
        if (parentWorld) {
          mw.multiplyMatrices(parentWorld, _tempLocalMatrix);
        } else {
          mw.copy(_tempLocalMatrix);
        }
      }

      this._dirty = false;
    }

    // Only traverse children if this node was updated OR subtree has dirty nodes
    if (needsUpdate || this._subtreeDirty) {

      for (const child of this.children) {
        // For attached nodes, re-read world matrix from instance collection before each child.
        // This avoids the aliased-temp problem: child recursion may overwrite _tempWorldMatrix,
        // so we fetch a fresh copy for each sibling.
        if (this.#instanceId !== null && this._instanceCollection) {
          this._instanceCollection.getTransform(this.#instanceId, _tempWorldMatrix);
          child.updateTransformWorld(needsUpdate, _tempWorldMatrix);
        } else {
          child.updateTransformWorld(needsUpdate, this._detachedState.matrixWorld);
        }
      }
      this._subtreeDirty = false;
    }
  }

  /**
   * Creates a new node from an existing instance in the instance collection.
   * @param {number} instanceId
   * @param {Model|Node3D} modelOrParentNode Parent node to which the new node should be added. If a model is provided the new node will be added to the scene graph root.
   * @returns {InstanceNode3D} The new node.
   */
  static fromInstance(instanceId, modelOrParentNode) {
    let parent = modelOrParentNode instanceof Model ? modelOrParentNode.getInstances().getSceneGraph() : modelOrParentNode;

    if (parent?.isAttached()) {
      const node = new InstanceNode3D();
      node.#instanceId = instanceId;
      parent.add(node);
      return node;
    } else {
      throw new Error("Cannot create InstanceNode3D from instance: parent node must be attached to an InstanceCollection3D");
    }
  }
}